import { useMemo, useState } from 'react';
import { AlertTriangle, Loader2, Search } from 'lucide-react';

import { useUserEntitiesQuery } from '@/client/api/admin/user-content.query';
import useDebounce from '@/client/hooks/useDebounce';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import EntityTable from './components/EntityTable';

const PAGE_SIZE = 12;

const entName = (ent: any): string =>
  ent?.Entity?.name_basic ||
  ent?.Entity?.name_marketing ||
  ent?.Entity?.name ||
  ent?.name_basic ||
  ent?.name_marketing ||
  ent?.name ||
  '';
const entEid = (ent: any): string => String(ent?.Entity?.eid ?? ent?.eid ?? '');

/**
 * All listings owned by this user, via `GET /api/admin/user/info/<uid>/listings`.
 * The endpoint returns the full set (no server-side paging/search), so search
 * and pagination are handled client-side here.
 */
const ListingsTab = ({ uid }: { uid: number }) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debSearch = useDebounce(search.trim().toLowerCase(), 300);

  const { data, isLoading, isError, isFetching } = useUserEntitiesQuery(uid);
  const all = useMemo(() => data?.entities ?? [], [data]);

  const filtered = useMemo(() => {
    if (!debSearch) return all;
    return all.filter(
      (e) =>
        entName(e).toLowerCase().includes(debSearch) ||
        entEid(e).includes(debSearch)
    );
  }, [all, debSearch]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const clampedPage = Math.min(page, totalPages);
  const pageItems = filtered.slice(
    (clampedPage - 1) * PAGE_SIZE,
    clampedPage * PAGE_SIZE
  );

  if (isError) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-6 text-center text-sm text-amber-900">
        <AlertTriangle className="mx-auto mb-2 h-6 w-6 text-amber-500" />
        <p className="font-medium">Couldn’t load this user’s listings.</p>
        <p className="mx-auto mt-1 max-w-md text-amber-800">
          This tab reads{' '}
          <code className="rounded bg-amber-100 px-1 py-0.5 text-xs">
            GET /api/admin/user/info/{uid}/listings
          </code>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            className="pl-9"
            placeholder="Search this user’s listings…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        {isFetching && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
      </div>

      <EntityTable
        entities={pageItems}
        isLoading={isLoading}
        emptyMessage={
          debSearch
            ? 'No listings match your search.'
            : 'This user has no listings.'
        }
      />

      {!isLoading && filtered.length > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">
            {filtered.length} listing{filtered.length === 1 ? '' : 's'} · page{' '}
            {clampedPage} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={clampedPage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={clampedPage >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListingsTab;
